/**
 * InlineTrackLane Component
 * Handles track timeline view with waveforms, MIDI clips, and right-click context menu
 */

import { useState, useRef, useEffect } from 'react';
import { WaveformClip } from '../waveform/WaveformClip';
import { MIDIClipView } from '../MIDIClipView';
import { Scissors, Copy, Trash2, Volume2, VolumeX } from 'lucide-react';

export const InlineTrackLane = ({ 
  track, 
  isSelected, 
  totalDuration, 
  zoom, 
  bpm, 
  currentTime, 
  isPlaying,
  onSelect, 
  onClipSelect, 
  onClipDoubleClick, 
  onClipMove, 
  onClipResize, 
  onCreateClip,
  onSplitClip, 
  onJoinSelectClip, 
  onSplitAudio, 
  clipEditMode, 
  selectedClipsForJoin,
  onFadeChange, 
  onLoopChange, 
  onTrimChange, 
  onSelectAudioClip, 
  selectedAudioClip, 
  onSeek, 
  snapEnabled,
  audioSelection, 
  onAudioSelectionChange,
  onDuplicate,
  onRemove,
  onToggleMute,
}) => {
  const hasAudio = track.audioBuffer && track.waveformData.length > 0;
  const hasMidiClips = track.midiClips && track.midiClips.length > 0;
  const isInstrumentTrack = track.type === 'instrument';
  const pixelsPerSecond = (100 * zoom) / totalDuration * 100;
  const isAudioClipSelected = selectedAudioClip?.trackId === track.id;
  
  // Context menu state
  const [ctxMenu, setCtxMenu] = useState(null);
  const menuRef = useRef(null);

  // Close context menu on outside click
  useEffect(() => {
    if (!ctxMenu) return;
    const close = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setCtxMenu(null);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [ctxMenu]);

  const handleContextMenu = (e) => {
    e.preventDefault();
    setCtxMenu({ x: e.clientX, y: e.clientY });
  };

  const getTrackColorHex = () => {
    const colorMap = {
      'from-cyan-500': 'rgba(6, 182, 212, 0.7)',
      'from-purple-500': 'rgba(168, 85, 247, 0.7)',
      'from-amber-500': 'rgba(245, 158, 11, 0.7)',
      'from-emerald-500': 'rgba(16, 185, 129, 0.7)',
      'from-rose-500': 'rgba(244, 63, 94, 0.7)',
      'from-indigo-500': 'rgba(99, 102, 241, 0.7)',
      'from-lime-500': 'rgba(132, 204, 22, 0.7)',
      'from-fuchsia-500': 'rgba(217, 70, 239, 0.7)',
    };
    const colorClass = track.color.split(' ')[0];
    return colorMap[colorClass] || 'rgba(139, 92, 246, 0.7)';
  };
  
  const handleDoubleClick = (e) => {
    if (!isInstrumentTrack) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const startTime = (x / rect.width) * totalDuration;
    onCreateClip?.(track.id, startTime);
  };
  
  return (
    <div
      className={`h-24 border-b border-zinc-800/50 relative backdrop-blur-sm ${
        isSelected ? 'bg-zinc-800/20' : 'bg-zinc-900/10'
      }`}
      onClick={onSelect}
      onDoubleClick={handleDoubleClick}
      onContextMenu={handleContextMenu}
      data-testid={`track-lane-${track.id}`}
    >
      {/* Track Label */}
      <div className="absolute left-2 top-2 text-xs text-zinc-500 z-10 flex items-center gap-2">
        {track.name}
        {isInstrumentTrack && (
          <span className="text-[10px] px-1 py-0.5 rounded bg-purple-500/20 text-purple-400">
            {track.instrumentType === 'drums' ? 'DRUMS' : 'SYNTH'}
          </span>
        )}
        {track.muted && (
          <span className="text-[10px] px-1 py-0.5 rounded bg-red-500/20 text-red-400">MUTED</span>
        )}
      </div>
      
      {/* Audio Region with WaveformClip */}
      {hasAudio && (
        <WaveformClip
          clip={{
            name: track.name,
            startTime: track.startTime,
            duration: track.duration,
            fadeIn: track.fadeIn || 0,
            fadeOut: track.fadeOut || 0,
            loopStart: track.clipLoopStart ?? null,
            loopEnd: track.clipLoopEnd ?? null,
            trimStart: track.trimStart || 0,
            trimEnd: track.trimEnd || 0,
            originalDuration: track.originalDuration || track.duration,
          }}
          track={track}
          audioBuffer={track.audioBuffer}
          waveformData={track.waveformData}
          isSelected={isAudioClipSelected}
          isPlaying={isPlaying}
          currentTime={currentTime}
          totalDuration={totalDuration}
          zoom={zoom}
          bpm={bpm}
          onSelect={() => {
            if (clipEditMode === 'split') {
              onSplitAudio?.(track.id);
            } else {
              onSelectAudioClip?.(track.id);
            }
          }}
          onFadeChange={(fades) => onFadeChange?.(track.id, fades)}
          onLoopChange={(loops) => onLoopChange?.(track.id, loops)}
          onTrimChange={(trim) => onTrimChange?.(track.id, trim)}
          onSeek={onSeek}
          onSelectionChange={(sel) => onAudioSelectionChange?.(track.id, sel)}
          selection={audioSelection?.trackId === track.id ? { start: audioSelection.start, end: audioSelection.end } : null}
          snapEnabled={snapEnabled}
          className={clipEditMode === 'split' ? 'cursor-crosshair' : ''}
        />
      )}
      
      {/* MIDI Clips */}
      {hasMidiClips && track.midiClips.map(clip => (
        <MIDIClipView
          key={clip.id}
          clip={clip}
          trackColor={getTrackColorHex()}
          pixelsPerSecond={pixelsPerSecond}
          totalDuration={totalDuration}
          isSelected={false}
          onSelect={() => onClipSelect?.(clip)}
          onMove={onClipMove}
          onResize={onClipResize}
          onDoubleClick={() => onClipDoubleClick?.(clip)}
          onSplit={() => onSplitClip?.(track.id, clip.id)}
          onJoinSelect={() => onJoinSelectClip?.(track.id, clip.id)}
          clipEditMode={clipEditMode}
          isSelectedForJoin={selectedClipsForJoin?.some(s => s.trackId === track.id && s.clipId === clip.id)}
        />
      ))}
      
      {/* Empty Track Placeholder */}
      {!hasAudio && !hasMidiClips && (
        <div className="absolute inset-0 flex items-center justify-center text-zinc-600 text-sm">
          {isInstrumentTrack ? 'Record MIDI or double-click to create clip' : 'Drop audio or click Record'}
        </div>
      )}

      {/* Right-click Context Menu */}
      {ctxMenu && (
        <div
          ref={menuRef}
          className="fixed z-[100] bg-zinc-800 border border-zinc-700 rounded-lg shadow-xl py-1 min-w-[180px] text-sm"
          style={{ left: ctxMenu.x, top: ctxMenu.y }}
          data-testid="track-context-menu"
        >
          {hasAudio && (
            <button
              onClick={() => { onSplitAudio?.(track.id); setCtxMenu(null); }}
              className="w-full px-3 py-2 text-left hover:bg-zinc-700 flex items-center gap-2 text-zinc-200"
              data-testid="ctx-split"
            >
              <Scissors className="w-3.5 h-3.5 text-cyan-400" />
              Split at Playhead
            </button>
          )}
          <button
            onClick={() => { onDuplicate?.(track.id); setCtxMenu(null); }}
            className="w-full px-3 py-2 text-left hover:bg-zinc-700 flex items-center gap-2 text-zinc-200"
            data-testid="ctx-duplicate"
          >
            <Copy className="w-3.5 h-3.5 text-blue-400" />
            Duplicate Track
          </button>
          <button
            onClick={() => { onToggleMute?.(track.id); setCtxMenu(null); }}
            className="w-full px-3 py-2 text-left hover:bg-zinc-700 flex items-center gap-2 text-zinc-200"
            data-testid="ctx-mute"
          >
            {track.muted ? (
              <><Volume2 className="w-3.5 h-3.5 text-green-400" /> Unmute Track</>
            ) : (
              <><VolumeX className="w-3.5 h-3.5 text-yellow-400" /> Mute Track</>
            )}
          </button>
          <div className="border-t border-zinc-700 my-1" />
          <button
            onClick={() => { onRemove?.(track.id); setCtxMenu(null); }}
            className="w-full px-3 py-2 text-left hover:bg-red-500/20 flex items-center gap-2 text-red-400"
            data-testid="ctx-delete"
          >
            <Trash2 className="w-3.5 h-3.5" />
            Delete Track
          </button>
        </div>
      )}
    </div>
  );
};

export default InlineTrackLane;
